import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';

type BillingPeriod = 'monthly' | 'yearly';

type RegionalPricing = {
  symbol: string;
  starter: { monthly: number; yearly: number };
  pro: { monthly: number; yearly: number };
  unlimited: { monthly: number; yearly: number };
  lifetime: { price: number };
  disclaimer: string | null;
};

const Section = styled.section`
  width: 100%;
  max-width: var(--landing-content-max);
  box-sizing: border-box;
  margin: 0 auto;
  padding: var(--landing-section-top) var(--landing-page-gutter) var(--landing-section-bottom);
  background: #ffffff;
`;

const Container = styled.div`
  width: 100%;
`;

const BillingToggle = styled.div`
  display: flex;
  justify-content: center;
  margin: 0;
`;

const BillingOptions = styled.div`
  display: flex;
  gap: 2px;
  padding: 3px;
  background: #f3f4f6;
  border-radius: 999px;
`;

const BillingOption = styled.button<{ active: boolean }>`
  padding: 8px 14px;
  color: ${props => props.active ? '#0f1116' : '#6b7280'};
  background: ${props => props.active ? '#ffffff' : 'transparent'};
  border: 0;
  border-radius: 999px;
  box-shadow: ${props => props.active ? '0 1px 2px rgba(15, 17, 22, .08)' : 'none'};
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
`;

const Table = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-top: 56px;
  overflow: visible;
  border: 1px solid #e2e0dc;
  border-radius: 24px;
  background: #ffffff;

  @media (max-width: 1080px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    border: 0;
    background: transparent;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const PlanCard = styled.article`
  position: relative;
  display: flex;
  min-width: 0;
  flex-direction: column;
  background: #ffffff;

  &:not(:first-of-type) {
    border-left: 1px solid #e2e0dc;
  }

  &:first-of-type { border-radius: 23px 0 0 23px; }
  &:last-of-type { border-radius: 0 23px 23px 0; }

  @media (max-width: 1080px) {
    border: 1px solid #e2e0dc !important;
    border-radius: 18px !important;
    margin: 0;
    box-shadow: none;
  }
`;
const HeaderWrapper = styled.div<{ bgImage?: string }>`
  position: relative;
  border-radius: inherit; /* inherit from PlanCard so corners are rounded */
  z-index: 1;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: ${props => props.bgImage ? `url(${props.bgImage})` : 'none'};
    background-size: 300px;
    background-position: right -90px top -40px;
    background-repeat: no-repeat;
    opacity: 0.1;
    mix-blend-mode: multiply;
    filter: grayscale(100%);
    border-radius: inherit;
    z-index: -1;
    pointer-events: none;
  }
`;

const CardHeader = styled.div`
  padding: 24px 24px 24px;
`;

const PlanName = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #161c2d;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px;
  font-weight: 600;
`;

const PlanMark = styled.span<{ color: string }>`
  display: grid;
  width: 22px;
  height: 22px;
  place-items: center;
  color: ${props => props.color};
  background: ${props => `${props.color}18`};
  border-radius: 7px;
  font-size: 14px;
  line-height: 1;
`;

const Offer = styled.div`
  display: flex;
  height: 194px;
  box-sizing: border-box;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 0 24px 24px;
`;

const PriceGroup = styled.div`
  display: grid;
  gap: 8px;
`;

const PriceSymbol = styled.span`
  font-family: 'Dirham', sans-serif;
  font-size: 0.85em;
  font-weight: 500;
  margin-right: 6px;
  line-height: 1;
`;

const Price = styled.div`
  display: flex;
  align-items: center;
  color: #0f1116;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 40px;
  font-weight: 500;
  letter-spacing: -.15rem;
  line-height: 1;
`;

const Period = styled.div`
  color: #6b7280;
  font-size: 12px;
  font-weight: 500;
`;

const Action = styled.a<{ primary?: boolean }>`
  display: inline-flex;
  width: 100%;
  height: 48px;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  color: ${props => props.primary ? '#ffffff' : 'var(--smush-cta-primary)'};
  background: ${props => props.primary ? 'var(--smush-cta-primary)' : 'var(--smush-cta-secondary)'};
  border: ${props => props.primary ? '1px solid var(--smush-cta-primary)' : '1px solid var(--smush-cta-secondary-border)'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: background-color .2s ease;

  &:hover {
    color: ${props => props.primary ? '#ffffff' : 'var(--smush-cta-primary)'};
    background: ${props => props.primary ? 'var(--smush-cta-primary-hover)' : 'var(--smush-cta-secondary-hover)'};
  }
`;

const Divider = styled.div`
  height: 1px;
  background: #eeece8;
`;

const FeatureList = styled.ul`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 16px;
  margin: 16px 0 0;
  padding: 0 24px 32px;
  list-style: none;
`;

const Category = styled.li`
  margin: 8px 0 0;
  color: #303238;
  font-size: 14px;
  font-weight: 600;
`;

const Feature = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: #303238;
  font-size: 14px;
  line-height: 1.4;

  svg {
    width: 18px;
    height: 18px;
    flex: 0 0 auto;
    color: #2d3647;
  }
`;

const Popular = styled.span`
  position: absolute;
  top: -28px;
  left: 0;
  display: flex;
  width: 100%;
  height: 28px;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: var(--smush-cta-primary);
  border-radius: 12px 12px 0 0;
  font-size: 12px;
  font-weight: 600;
`;

const Disclaimer = styled.p`
  max-width: 560px;
  margin: 20px auto 0;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.5;
  text-align: center;
`;

const BNPLBanner = styled.div`
  margin: 32px auto 0;
  padding: 12px 24px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #334155;
  font-size: 14px;
  font-weight: 500;
  max-width: fit-content;

  strong {
    color: #0f1116;
    background: #ffffff;
    padding: 4px 12px;
    border-radius: 999px;
    border: 1px solid #e2e0dc;
    box-shadow: 0 1px 2px rgba(15, 17, 22, .08);
  }
`;

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="m5 12 4 4L19 6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Cross = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

type PlanTier = 'Starter' | 'Pro' | 'Unlimited';

type ComparisonItem = {
  name: string;
  values: Record<PlanTier, string | boolean>;
};

const comparison: Array<{ category: string; items: ComparisonItem[] }> = [
  {
    category: 'Usage Limits',
    items: [
      { name: 'Chatbots', values: { Starter: '1', Pro: '3', Unlimited: 'Unlimited' } },
      { name: 'Lead Forms', values: { Starter: '1', Pro: '10', Unlimited: 'Unlimited' } },
      { name: 'Smart Responses', values: { Starter: '1,000/mo', Pro: '50,000/mo', Unlimited: '500k/mo' } },
      { name: 'File Uploads', values: { Starter: '20', Pro: '100', Unlimited: '500' } },
      { name: 'Training URLs', values: { Starter: '30', Pro: '1,000', Unlimited: '5,000' } },
    ],
  },
  {
    category: 'Platform Features',
    items: [
      { name: 'Analytics', values: { Starter: 'Basic', Pro: 'Advanced', Unlimited: 'Pro' } },
      { name: 'FAQ Items', values: { Starter: '3', Pro: 'Unlimited', Unlimited: 'Unlimited' } },
      { name: 'History', values: { Starter: '3 Months', Pro: '1 Year', Unlimited: '3 Years' } },
      { name: 'Intent Workflows', values: { Starter: false, Pro: '30', Unlimited: 'Unlimited' } },
      { name: 'Human Handoff', values: { Starter: false, Pro: false, Unlimited: 'Advanced' } },
    ],
  },
  {
    category: 'Advanced',
    items: [
      { name: 'AI Model', values: { Starter: 'Standard', Pro: 'Premium', Unlimited: 'Premium' } },
      { name: 'Bring Your Own LLM', values: { Starter: false, Pro: true, Unlimited: true } },
      { name: 'White Labeling', values: { Starter: false, Pro: true, Unlimited: true } },
      { name: 'Integrations', values: { Starter: false, Pro: true, Unlimited: true } },
      { name: 'Setup & Onboarding', values: { Starter: 'Included', Pro: 'Included', Unlimited: 'Included' } },
    ],
  },
];

const foundersFeatures = [
  '5 chatbots and 10 lead forms',
  '100,000 smart responses / month',
  '250 file uploads and 2,000 training URLs',
  'Advanced analytics with CSV export',
  '1 year conversation history and 30 workflows',
  'Premium AI model and priority support',
  'Private founders group and setup included',
];

const PricingCardsExtended: React.FC = () => {
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [bnplProvider, setBnplProvider] = useState<'Klarna' | 'Tabby' | null>(null);
  const [pricing, setPricing] = useState<RegionalPricing>({
    symbol: '$',
    starter: { monthly: 24, yearly: 228 },
    pro: { monthly: 38, yearly: 360 },
    unlimited: { monthly: 125, yearly: 1200 },
    lifetime: { price: 947 },
    disclaimer: null,
  });

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const region = new URLSearchParams(window.location.search).get('region');
        
        if (region) {
          const r = region.toLowerCase();
          if (['us', 'usa', 'uk', 'gb', 'eu', 'europe', 'au', 'australia', 'ca', 'canada'].includes(r)) {
            setBnplProvider('Klarna');
          } else if (['ae', 'uae', 'sa', 'saudi', 'saudi arabia'].includes(r)) {
            setBnplProvider('Tabby');
          }
        }

        const response = await fetch(region ? `/api/pricing?region=${region}` : '/api/pricing');
        if (!response.ok) return;
        const data = await response.json();
        setPricing({
          symbol: data.symbol,
          starter: data.plans.starter,
          pro: data.plans.pro,
          unlimited: data.plans.unlimited,
          lifetime: data.plans.lifetime,
          disclaimer: data.disclaimer,
        });
      } catch {
        // Keep the USD fallback visible if regional pricing is unavailable.
      }
    };
    fetchPricing();
  }, []);

  const planPrice = (plan: 'starter' | 'pro' | 'unlimited') =>
    billingPeriod === 'monthly' ? pricing[plan].monthly : Math.round(pricing[plan].yearly / 12);

  const pricePeriod = billingPeriod === 'monthly' ? 'per month' : 'per month, billed yearly';

  const plans = [
    {
      name: 'Starter', mark: '✦', color: '#64748b', price: planPrice('starter'),
      period: pricePeriod, href: '/get-started?plan=starter', action: 'Get started', popular: false,
      bgImage: '/images/pricing/starter_bg.jpg'
    },
    {
      name: 'Pro', mark: '✦', color: '#6d4aff', price: planPrice('pro'),
      period: pricePeriod, href: '/get-started?plan=pro', action: 'Get started', popular: true,
      bgImage: '/images/pricing/pro_bg.jpg'
    },
    {
      name: 'Unlimited', mark: '✦', color: '#0f766e', price: planPrice('unlimited'),
      period: pricePeriod, href: '/get-started?plan=unlimited', action: 'Get started', popular: false,
      bgImage: '/images/pricing/unlimited_bg.jpg'
    },
    {
      name: 'Founders Edition', mark: '✦', color: '#b45309', price: pricing.lifetime.price,
      period: 'one-time payment', href: '/launch-deal', action: 'View launch deal', popular: false,
      bgImage: '/images/pricing/founder_bg.jpg'
    },
  ] as const;

  return (
    <Section id="plans">
      <Container>
        <BillingToggle>
          <BillingOptions aria-label="Billing period">
            <BillingOption active={billingPeriod === 'monthly'} onClick={() => setBillingPeriod('monthly')}>Monthly</BillingOption>
            <BillingOption active={billingPeriod === 'yearly'} onClick={() => setBillingPeriod('yearly')}>Yearly</BillingOption>
          </BillingOptions>
        </BillingToggle>
        <Table>
          {plans.map(plan => (
            <PlanCard key={plan.name}>
              {plan.popular && <Popular>Popular</Popular>}
              <HeaderWrapper bgImage={plan.bgImage}>
                <CardHeader>
                  <PlanName><PlanMark color={plan.color}>{plan.mark}</PlanMark>{plan.name}</PlanName>
                </CardHeader>
                <Offer>
                  <PriceGroup>
                    <Price><PriceSymbol>{pricing.symbol}</PriceSymbol>{plan.price}</Price>
                    <Period>
                      {plan.period}
                      {bnplProvider === 'Tabby' && plan.name === 'Founders Edition' && (
                        <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px', color: '#161c2d', fontWeight: 600 }}>
                          or 4 interest-free payments with
                          <img src="https://mintcdn.com/tabby-5f40add6/1f-EIKwKexPNgWGm/logo/tabby-badge.svg" alt="Tabby" style={{ height: '20px' }} />
                        </div>
                      )}
                    </Period>
                  </PriceGroup>
                  <Action primary={plan.popular} href={plan.href}>{plan.action}</Action>
                </Offer>
              </HeaderWrapper>
              <Divider />
              <FeatureList>
                {plan.name === 'Founders Edition' ? foundersFeatures.map(feature => (
                  <Feature key={feature}><Check />{feature}</Feature>
                )) : comparison.map(section => (
                  <React.Fragment key={section.category}>
                    <Category>{section.category}</Category>
                    {section.items.map(item => {
                      const value = item.values[plan.name];
                      return (
                        <Feature key={item.name}>
                          {value === false ? <Cross /> : <Check />}
                          {item.name}{typeof value === 'string' ? `: ${value}` : ''}
                        </Feature>
                      );
                    })}
                  </React.Fragment>
                ))}
              </FeatureList>
            </PlanCard>
          ))}
        </Table>

        {bnplProvider && (
          <BNPLBanner>
            <span>Buy Now, Pay Later available in your region with</span>
            {bnplProvider === 'Tabby' ? (
              <img src="https://mintcdn.com/tabby-5f40add6/1f-EIKwKexPNgWGm/logo/tabby-badge.svg" alt="Tabby" style={{ height: '24px' }} />
            ) : (
              <strong>{bnplProvider}</strong>
            )}
          </BNPLBanner>
        )}

        {pricing.disclaimer && <Disclaimer>{pricing.disclaimer}</Disclaimer>}
      </Container>
    </Section>
  );
};

export default PricingCardsExtended;
